/**
 * A linha de ações da tela do laudo eletrônico (E74.F3): baixar PDF e DOCX, voltar à mesa e os
 * caminhos para a conferência e a correção do rascunho.
 *
 * F-E162.F2-02 (QA-21): os dois botões de baixar seguem a prontidão que a tela já mostra
 * (`bloqueioDoDownload`). Parados, dizem o porquê logo abaixo, e o texto descreve os botões para
 * quem navega por leitor de tela. Os caminhos de navegação nunca param.
 */
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';

import type { BloqueioDoDownload } from './bloqueioDoDownload';

const t = ptBR.laudoEletronico;
const ID_DO_BLOQUEIO = 'laudo-eletronico-download-bloqueio';

export type FormatoDoLaudo = 'pdf' | 'docx';

export interface AcoesDoLaudoProps {
  caseId: string;
  baixando: FormatoDoLaudo | null;
  /** `null` = baixar liberado; senão, o porquê de os botões de baixar esperarem. */
  bloqueio: BloqueioDoDownload | null;
  onBaixar: (formato: FormatoDoLaudo) => void;
}

export function AcoesDoLaudo({
  caseId,
  baixando,
  bloqueio,
  onBaixar,
}: AcoesDoLaudoProps): JSX.Element {
  const navigate = useNavigate();
  const parado = baixando !== null || bloqueio !== null;
  const descricao = bloqueio !== null ? ID_DO_BLOQUEIO : undefined;
  return (
    <div className="mt-sc-6 flex flex-col gap-sc-2">
      <div className="flex flex-wrap gap-sc-2">
        <Button
          type="button"
          onClick={() => onBaixar('pdf')}
          disabled={parado}
          aria-describedby={descricao}
          data-testid="laudo-eletronico-pdf"
        >
          {baixando === 'pdf' ? t.baixando : t.baixarPdf}
        </Button>
        <Button
          type="button"
          variant="secondary"
          onClick={() => onBaixar('docx')}
          disabled={parado}
          aria-describedby={descricao}
          data-testid="laudo-eletronico-docx"
        >
          {baixando === 'docx' ? t.baixando : t.baixarDocx}
        </Button>
        <Button
          type="button"
          variant="ghost"
          onClick={() => navigate(`/casos/${caseId}/mesa-eletronica`)}
        >
          {t.irParaMesa}
        </Button>
        {/* E159.F3 (GUS-153): conferir o texto antes de assinar e entregar. */}
        <Button
          type="button"
          variant="ghost"
          onClick={() => navigate(`/casos/${caseId}/saidas`)}
          data-testid="laudo-eletronico-saidas"
        >
          {ptBR.conferenciaDasSaidas.linkParaAConferencia}
        </Button>
        {/* E171.F3 (GUS-145): a redação do exame e do risco se corrige na mesa (E202.F3). */}
        <Button
          type="button"
          variant="ghost"
          onClick={() => navigate(`/casos/${caseId}/mesa`)}
          data-testid="laudo-eletronico-correcao"
        >
          {ptBR.editorManual.corrigirNaMesa}
        </Button>
      </div>
      {bloqueio !== null ? (
        <p
          id={ID_DO_BLOQUEIO}
          role="status"
          className="text-body-sm text-fg-muted"
          data-testid={ID_DO_BLOQUEIO}
          data-estado={bloqueio.estado}
        >
          {bloqueio.texto}
        </p>
      ) : null}
    </div>
  );
}
